import { useState, useEffect } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import axios from "axios"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import CostSales from "../../CostSales"
import { neumorph } from "../neumorph"
import { usePeriod } from "../PeriodContext"
import { formatCurrency } from "../KpiHeader"

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend)

const RevenueGpCard = ({ supplier, retailer }) => {
  const { startDate, endDate } = usePeriod()
  const [loading, setLoading] = useState(false)
  const [trend, setTrend] = useState([])
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/insights/cost-sales`,
          { supplier, retailer, userid: localStorage.getItem("userid"), startDate, endDate },
          { signal: controller.signal }
        )
        if (res.data?.status === 1) setTrend(res.data.data?.trend || [])
        else setTrend([])
      } catch (err) {
        if (!axios.isCancel(err)) console.error("RevenueGpCard fetch error:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer, startDate, endDate])

  const totalRev = trend.reduce((s, r) => s + (parseFloat(r.total_revenue) || 0), 0)
  const totalCost = trend.reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0)
  const gpPct = totalRev > 0 ? ((totalRev - totalCost) / totalRev) * 100 : 0

  const chartData = trend.length
    ? {
        labels: trend.map((r) => `${r.month_name} ${String(r.year).slice(-2)}`),
        datasets: [
          {
            type: "bar",
            label: "Revenue",
            data: trend.map((r) => parseFloat(r.total_revenue) || 0),
            backgroundColor: "rgba(63, 158, 126, 0.7)",
            borderRadius: 4,
            yAxisID: "y",
            order: 2,
          },
          {
            type: "bar",
            label: "Cost",
            data: trend.map((r) => parseFloat(r.total_cost) || 0),
            backgroundColor: "rgba(229, 115, 115, 0.6)",
            borderRadius: 4,
            yAxisID: "y",
            order: 2,
          },
          {
            type: "line",
            label: "GP %",
            data: trend.map((r) => parseFloat(r.gp_percent) || 0),
            borderColor: neumorph.accentTealDark,
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.3,
            yAxisID: "y1",
            order: 1,
          },
        ],
      }
    : null

  const fmtCompactRand = (v) => {
    if (v >= 1_000_000) return `R${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `R${(v / 1_000).toFixed(0)}k`
    return `R${v}`
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
      datalabels: { display: false }, // suppress global datalabels plugin in mini cards
    },
    scales: {
      x: { display: true, grid: { display: false }, ticks: { font: { size: 9 }, color: neumorph.textMuted } },
      y: {
        display: true,
        beginAtZero: true,
        grid: { color: "rgba(174,184,200,0.15)", drawBorder: false },
        ticks: { font: { size: 9 }, color: neumorph.textMuted, maxTicksLimit: 4, callback: fmtCompactRand },
      },
      y1: { display: false, position: "right", min: 0, max: 100 },
    },
  }

  return (
    <>
      <BentoCard
        title="Revenue vs Cost · GP%"
        subtitle="Bars = revenue/cost · line = GP%"
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        <Flex direction="column" h="100%" justify="space-between">
          <Flex gap={3} mb={2}>
            <Box>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                Revenue
              </Text>
              <Text fontSize="md" fontWeight="700" color={neumorph.accentGreen}>
                {formatCurrency(totalRev)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                GP %
              </Text>
              <Text
                fontSize="md"
                fontWeight="700"
                color={gpPct >= 20 ? neumorph.accentGreen : neumorph.accentRed}
              >
                {gpPct.toFixed(1)}%
              </Text>
            </Box>
          </Flex>
          {chartData ? (
            <Box h="120px">
              <Bar data={chartData} options={chartOptions} />
            </Box>
          ) : (
            <Text fontSize="xs" color={neumorph.textMuted}>
              No trend data
            </Text>
          )}
        </Flex>
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Cost & Gross Profit Analysis">
        <CostSales supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default RevenueGpCard
